import { useMemo, useState } from "react";
import { Link, Redirect } from "wouter";
import { useAuth } from "../hooks/useAuth";
import {
  useCustomerInboxList,
  useMarkInboxAllRead,
  type CustomerInboxListResponse,
} from "@orbit/api-client-react";
import { Wordmark } from "../components/Brand";
import { BottomNav } from "../components/BottomNav";

/**
 * /inbox — Phase 15 customer in-app inbox.
 *
 * Two tabs: All vs Unread. Cursor-paginated (we keep accumulated rows
 * locally and append as the user pulls "Load older"). The mark-all-read
 * button lives at the top-right and only fires when there's at least
 * one unread row in view.
 */
type Tab = "all" | "unread";

type InboxItem = CustomerInboxListResponse["items"][number];

export function InboxPage() {
  const { isAuthenticated } = useAuth();
  const [tab, setTab] = useState<Tab>("all");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState<InboxItem[]>([]);
  const [seenCursor, setSeenCursor] = useState<string | undefined>(undefined);

  const listQ = useCustomerInboxList(
    { cursor, limit: 20, unreadOnly: tab === "unread" },
    {
      enabled: isAuthenticated,
    },
  );

  // Reset local pagination state when the tab toggles.
  const resetForTab = (next: Tab) => {
    if (next === tab) return;
    setTab(next);
    setCursor(undefined);
    setLoaded([]);
    setSeenCursor(undefined);
  };

  // Append fresh page into the accumulator (idempotent on cursor).
  if (
    listQ.data &&
    listQ.data.items.length > 0 &&
    seenCursor !== (cursor ?? "first")
  ) {
    setLoaded((prev) => {
      const existing = new Set(prev.map((r) => r.id));
      const additions = listQ.data!.items.filter((r) => !existing.has(r.id));
      return prev.concat(additions);
    });
    setSeenCursor(cursor ?? "first");
  }

  const markAll = useMarkInboxAllRead();
  const hasUnread = useMemo(
    () => loaded.some((m) => m.readAt == null),
    [loaded],
  );

  if (!isAuthenticated) return <Redirect to="/login" />;

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link
            href="/profile"
            className="text-sm text-ink-dark hover:text-navy"
          >
            Profile
          </Link>
        </header>

        <div className="mt-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-navy">Inbox</h1>
          {hasUnread && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="text-xs font-semibold text-navy hover:text-navy/80 disabled:opacity-50"
              data-testid="button-mark-all-read"
            >
              {markAll.isPending ? "Marking…" : "Mark all read"}
            </button>
          )}
        </div>

        <div className="mt-4 flex gap-1 rounded-full bg-navy/5 p-1 text-sm">
          <TabButton
            active={tab === "all"}
            onClick={() => resetForTab("all")}
            label="All"
            testId="tab-inbox-all"
          />
          <TabButton
            active={tab === "unread"}
            onClick={() => resetForTab("unread")}
            label="Unread"
            testId="tab-inbox-unread"
          />
        </div>

        {listQ.isError && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Couldn't load your inbox. Pull to refresh.
          </div>
        )}

        {listQ.isLoading && loaded.length === 0 ? (
          <div className="mt-6 space-y-3">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        ) : loaded.length === 0 ? (
          <div className="mt-6 card text-center">
            <p className="text-sm text-ink-dark">
              {tab === "unread"
                ? "Nothing unread. You're all caught up."
                : "No messages yet. We'll let you know when your vouchers are about to expire or when a merchant has news."}
            </p>
          </div>
        ) : (
          <ul
            className="mt-4 space-y-2"
            data-testid="list-inbox"
            aria-label="Inbox messages"
          >
            {loaded.map((m) => (
              <InboxRow key={m.id} item={m} />
            ))}
          </ul>
        )}

        {listQ.data?.nextCursor && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setCursor(listQ.data!.nextCursor ?? undefined)}
              disabled={listQ.isFetching}
              className="text-xs font-semibold text-navy hover:text-navy/80 disabled:opacity-50"
              data-testid="button-load-older"
            >
              {listQ.isFetching ? "Loading…" : "Load older"}
            </button>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function TabButton(props: {
  active: boolean;
  onClick: () => void;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`flex-1 rounded-full px-4 py-1.5 text-center text-xs font-semibold transition ${
        props.active
          ? "bg-white text-navy shadow-sm"
          : "text-ink-dark hover:text-navy"
      }`}
      data-testid={props.testId}
      aria-pressed={props.active}
    >
      {props.label}
    </button>
  );
}

function SkeletonRow() {
  return (
    <div className="card animate-pulse">
      <div className="h-4 w-1/3 rounded bg-navy/10" />
      <div className="mt-2 h-3 w-3/4 rounded bg-navy/5" />
    </div>
  );
}

function InboxRow({ item }: { item: InboxItem }) {
  const unread = item.readAt == null;
  const sentDate = new Date(item.createdAt);
  const subtitle = formatRelative(sentDate);
  const author = formatAuthor(item);
  return (
    <li>
      <Link
        href={`/inbox/${item.id}`}
        className={`block card transition ${
          unread ? "border-l-4 border-l-amber" : ""
        }`}
        data-testid={`row-inbox-${item.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-navy/60">
              {author} • {labelForType(item.messageType)}
            </p>
            <h2
              className={`mt-1 truncate text-sm ${
                unread ? "font-bold text-navy" : "font-medium text-navy/80"
              }`}
            >
              {item.subject}
            </h2>
            <p className="mt-1 line-clamp-2 text-xs text-ink-dark">
              {item.body}
            </p>
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-ink-dark/70">
            {subtitle}
          </span>
        </div>
      </Link>
    </li>
  );
}

function labelForType(t: InboxItem["messageType"]): string {
  switch (t) {
    case "VOUCHER_EXPIRING_SOON":
      return "Voucher";
    case "MARKETPLACE_CLAIM_CREDITED":
      return "Claim";
    case "TENANT_MESSAGE":
      return "Merchant";
    case "M42_ANNOUNCEMENT":
      return "Orbit";
    default:
      return "Message";
  }
}

function formatAuthor(item: InboxItem): string {
  if (item.authorType === "TENANT" && item.authorTenantName) {
    return item.authorTenantName;
  }
  if (item.authorType === "M42_PLATFORM") return "Orbit";
  return "System";
}

function formatRelative(d: Date): string {
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
